import React from "react";
import {
  Close,
  LocationOn,
  Login,
  Logout,
  TimerOutlined,
  WarningAmber,
} from "@mui/icons-material";

const formatDateTime = (timestamp) => {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  return (
    d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    ", " +
    d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  );
};

const formatDuration = (durationMs) => {
  if (!durationMs || durationMs <= 0) return "—";

  const totalSeconds = Math.floor(durationMs / 1000);

  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  let result = [];

  if (hrs > 0) result.push(`${hrs}h`);
  if (mins > 0) result.push(`${mins}m`);
  if (secs > 0) result.push(`${secs}s`);

  return result.join(" ") || "0s";
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    borderRadius: 8,
    width: "90%",
    maxWidth: 680,
    maxHeight: "85vh",
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    background: "#0F3C75",
    color: "#fff",
    padding: "12px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  headerSub: {
    fontSize: 12.5,
    opacity: 0.7,
    marginTop: 2,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    padding: 4,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
  },
  body: {
    padding: 0,
    overflowY: "auto",
    flex: 1,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    background: "#f8f9fa",
    padding: "10px 14px",
    textAlign: "left",
    fontWeight: 600,
    color: "#2c3e50",
    fontSize: 12.5,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    borderBottom: "2px solid #eee",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "11px 14px",
    borderBottom: "1px solid #f0f0f0",
    color: "#333",
    verticalAlign: "middle",
  },
  rowEven: {
    background: "#fafbfc",
  },
  missingChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "#fff3e0",
    color: "#e65100",
    fontSize: 12.5,
    fontWeight: 600,
    padding: "3px 9px",
    borderRadius: 10,
  },
  durationChip: (hasValue) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    background: hasValue ? "#e8f5e9" : "#f5f5f5",
    color: hasValue ? "#2e7d32" : "#999",
    fontSize: 13,
    fontWeight: 600,
    padding: "4px 11px",
    borderRadius: 12,
  }),
  emptyState: {
    padding: 50,
    textAlign: "center",
    color: "#999",
    fontSize: 14,
  },
  footer: {
    borderTop: "1px solid #eee",
    padding: "11px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 12.2,
    color: "#666",
    background: "#f9fafb",
  },
  srNo: {
    fontWeight: 600,
    color: "#2c3e50",
    fontSize: 13,
    width: 46,
    textAlign: "center",
  },
};

const CheckinCheckoutModal = ({
  open,
  onClose,
  geofenceData,
  geofenceName = "Geofence",
}) => {
  if (!open) return null;

  const sessions = geofenceData?.trailData || [];
  const validSessions = sessions.filter(Boolean);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <LocationOn style={{ fontSize: 22, color: "#fff" }} />
            <div>
              <div style={styles.headerTitle}>{geofenceName}</div>
              <div style={styles.headerSub}>
                {validSessions.length} session
                {validSessions.length !== 1 ? "s" : ""} recorded
              </div>
            </div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>
            <Close style={{ fontSize: 21 }} />
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {validSessions.length === 0 ? (
            <div style={styles.emptyState}>
              No sessions found for this geofence.
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, textAlign: "center" }}>Sl No.</th>
                  <th style={styles.th}>
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 5 }}
                    >
                      <Login style={{ fontSize: 15 }} /> Check-In
                    </span>
                  </th>
                  <th style={styles.th}>
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 5 }}
                    >
                      <Logout style={{ fontSize: 15 }} /> Check-Out
                    </span>
                  </th>
                  <th style={styles.th}>
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 5 }}
                    >
                      <TimerOutlined style={{ fontSize: 15 }} /> Duration
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {validSessions.map((session, idx) => {
                  const checkinTime = session.checkInTime;
                  const checkoutTime = session.checkOutTime;

                  // Missing logic as per your instruction
                  const checkinMissing = !checkinTime;
                  const checkoutMissing = !checkoutTime;
                  const durationMissing =
                    !session.duration || session.duration <= 0;

                  return (
                    <tr
                      key={session.id || idx}
                      style={idx % 2 === 0 ? {} : styles.rowEven}
                    >
                      <td style={{ ...styles.td, ...styles.srNo }}>
                        {idx + 1}
                      </td>

                      {/* Check-In */}
                      <td style={styles.td}>
                        {checkinMissing ? (
                          <span style={styles.missingChip}>
                            <WarningAmber style={{ fontSize: 13 }} /> Missing
                          </span>
                        ) : (
                          formatDateTime(checkinTime)
                        )}
                      </td>

                      {/* Check-Out */}
                      <td style={styles.td}>
                        {checkoutMissing ? (
                          <span style={styles.missingChip}>
                            <WarningAmber style={{ fontSize: 13 }} /> Missing
                          </span>
                        ) : (
                          formatDateTime(checkoutTime)
                        )}
                      </td>

                      {/* Duration */}
                      <td style={styles.td}>
                        <span style={styles.durationChip(!durationMissing)}>
                          <TimerOutlined style={{ fontSize: 15 }} />
                          {durationMissing
                            ? "—"
                            : formatDuration(session.duration)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        {/* <div style={styles.footer}>
          <span>
            <WarningAmber
              style={{
                fontSize: 13,
                color: "#e65100",
                verticalAlign: "middle",
                marginRight: 5,
              }}
            />
            Missing data indicates incomplete session (device switched off or
            connectivity issue)
          </span>
        </div> */}
      </div>
    </div>
  );
};

export default CheckinCheckoutModal;
